import { useState, useCallback, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { Modal, Button, Space } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import type { GridWidgetInstance, GridWidgetType } from "../types";
import type { ComponentSpec } from "../Processing/types";
import { useDataPipeline } from "../DataPipeline";
import { useAiAccess } from "../useAiAccess";
import { useDashboardActions } from "../Contexts/dashboardHooks";
import { getWidgetDef } from "../widgetRegistry";
import { TemplatePicker, type WidgetTemplate } from "./TemplatePicker";
import { ChartTemplatePicker } from "./ChartTemplatePicker";
import { createStateFromTemplate, type ChartTemplateKey, type PlotFormState } from "./PlotConfigurator/plotState";
import { useCountMetricOptions } from "./shared/CountIncludePicker";

type ModalStep = "pick-template" | "pick-chart-template" | "configure";

interface AddWidgetModalProps {
  open: boolean;
  onClose: () => void;
  existingWidget?: GridWidgetInstance;
}

export function AddWidgetModal({ open, onClose, existingWidget }: AddWidgetModalProps) {
  const sandboxAvailability = useSelector(({ team }: any) => team?.info?.sandbox_widget_availability);
  const hasAiAccess = useAiAccess();
  // The AI widget is gated on DataViz AI, not on the sandbox widget flags, so
  // its card locks independently of whatever the backend sent for the rest.
  const widgetAvailability = useMemo(
    () => (sandboxAvailability ? { ...sandboxAvailability, ai_insights: hasAiAccess } : undefined),
    [sandboxAvailability, hasAiAccess],
  );
  const pipeline = useDataPipeline();
  const { addWidget, editWidget } = useDashboardActions();

  // Counts stay out of `pipeline.getMetrics()` — that list also feeds
  // `resolveSpecBaselines`, and a count has no baseline — but every configurator needs
  // them here, to name the metric a widget plots. Without a count in this list the
  // lookups that build a widget title, a series name and a preview label all miss, and
  // a count widget opens with no title at all.
  const baseMetrics = useMemo(() => pipeline.getMetrics(), [pipeline]);
  const countOptions = useCountMetricOptions();
  const metrics = useMemo(
    () => [...baseMetrics, ...countOptions], [baseMetrics, countOptions]);
  const getSubjectEntities = pipeline.getSubjectEntities;

  const isEditing = existingWidget != null;

  const [step, setStep] = useState<ModalStep>(
    isEditing ? "configure" : "pick-template",
  );
  const [selectedType, setSelectedType] = useState<GridWidgetType | null>(
    isEditing ? existingWidget.type : null,
  );
  const [currentSpec, setCurrentSpec] = useState<ComponentSpec | null>(
    existingWidget?.spec ?? null,
  );
  const [chartInitialState, setChartInitialState] = useState<PlotFormState | undefined>(undefined);

  useEffect(() => {
    if (open) {
      if (isEditing) {
        setStep("configure");
        setSelectedType(existingWidget.type);
        setCurrentSpec(existingWidget.spec ?? null);
        setChartInitialState(undefined);
      } else {
        setStep("pick-template");
        setSelectedType(null);
        setCurrentSpec(null);
        setChartInitialState(undefined);
      }
    }
  }, [open, isEditing, existingWidget]);

  const handleClose = useCallback(() => {
    setStep("pick-template");
    setSelectedType(null);
    setCurrentSpec(null);
    setChartInitialState(undefined);
    onClose();
  }, [onClose]);

  const handleTemplateSelect = useCallback(
    (template: WidgetTemplate) => {
      const def = getWidgetDef(template.type);
      if (template.type === "chart") {
        setSelectedType("chart");
        setStep("pick-chart-template");
        return;
      }
      if (def?.configurator) {
        setSelectedType(template.type);
        setStep("configure");
        return;
      }
      // No configurator — add widget immediately
      addWidget(template.type);
      handleClose();
    },
    [addWidget, handleClose],
  );

  const handleChartTemplateSelect = useCallback(
    (key: ChartTemplateKey | "scratch") => {
      setChartInitialState(key === "scratch" ? undefined : createStateFromTemplate(key, metrics));
      setStep("configure");
    },
    [metrics],
  );

  const handleBack = useCallback(() => {
    if (step === "configure" && selectedType === "chart" && !isEditing) {
      setStep("pick-chart-template");
      setCurrentSpec(null);
      setChartInitialState(undefined);
    } else if (step === "pick-chart-template") {
      setStep("pick-template");
      setSelectedType(null);
    } else {
      setStep("pick-template");
      setSelectedType(null);
      setCurrentSpec(null);
      setChartInitialState(undefined);
    }
  }, [step, selectedType, isEditing]);

  const handleConfirm = useCallback(() => {
    if (!selectedType) return;

    if (isEditing && existingWidget) {
      if (currentSpec) editWidget(existingWidget.id, currentSpec);
    } else {
      addWidget(selectedType, currentSpec ?? undefined);
    }
    handleClose();
  }, [selectedType, currentSpec, isEditing, existingWidget, addWidget, editWidget, handleClose]);

  const handleSpecChange = useCallback((spec: ComponentSpec | null) => {
    setCurrentSpec(spec);
  }, []);

  const widgetDef = selectedType ? getWidgetDef(selectedType) : null;
  const configurator = widgetDef?.configurator;
  const isConfiguring = step === "configure" && configurator != null;

  const wide = isConfiguring && (configurator?.wide ?? false);
  // Template-picker steps: wider modal with a fixed-height, scrollable widget list.
  const isTemplateStep = step === "pick-template" || step === "pick-chart-template";
  const modalTitle =
    step === "pick-chart-template"
      ? "Add Plot"
      : isConfiguring
        ? `${isEditing ? "Edit" : "Add"} ${configurator!.modalTitle}`
        : "Add Widget";

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={
        isConfiguring ? (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            {!isEditing ? (
              <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
                Back
              </Button>
            ) : (
              <span />
            )}
            <Space>
              <Button onClick={handleClose}>Cancel</Button>
              <Button type="primary" onClick={handleConfirm} disabled={!currentSpec}>
                {isEditing ? "Save Changes" : "Add Widget"}
              </Button>
            </Space>
          </div>
        ) : null
      }
      title={modalTitle}
      width={wide ? 1200 : isTemplateStep ? "65%" : 560}
      destroyOnClose
      className={`awm-modal ${wide ? "awm-modal-wide" : ""} ${isTemplateStep ? "awm-modal-templates" : ""}`}
    >
      {step === "pick-template" && (
        <TemplatePicker onSelect={handleTemplateSelect} availability={widgetAvailability} />
      )}
      {step === "pick-chart-template" && (
        <ChartTemplatePicker onSelect={handleChartTemplateSelect} />
      )}
      {step === "configure" && configurator && (
        <configurator.Component
          onSpecChange={handleSpecChange}
          existingSpec={isEditing ? existingWidget?.spec : undefined}
          widgetId={isEditing ? existingWidget?.id : undefined}
          initialFormState={chartInitialState}
          metrics={metrics}
          getSubjectEntities={getSubjectEntities}
        />
      )}
    </Modal>
  );
}
